import React from 'react';
import { useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { getRelativeTime } from 'helpers/dates';
import { CommunityActivityAlert } from 'types/recommendations';

const Column = styled.div`
  flex: 1;
  min-width: 0;
  padding-right: 24px;
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-flow: row;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding-top: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--line-gray);
`;

const ColumnTitle = styled.div`
  font-family: var(--font-bold);
  font-size: 14px;
`;

const ColumnLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
  white-space: nowrap;
  text-decoration: none;
`;

const AlertsList = styled.div`
  margin-top: 10px;
  padding-bottom: 30px;
`;

const AlertRow = styled.div`
  display: flex;
  flex-flow: row;
  gap: 15px;
  padding: 10px 0;
  border-bottom: solid 1px var(--line-gray);
`;

const AlertAccent = styled.div`
  width: 4px;
  border-radius: 4px;
  background: linear-gradient(to bottom, var(--amber-light), var(--inactive-orange));
`;

const AlertContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-flow: row;
  justify-content: space-between;
  gap: 10px;
`;

const AlertTitle = styled.div`
  font-size: 13px;
`;

const AlertTime = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  font-size: 12px;
  padding: 16px 0;
`;

type Props = {
  alerts: CommunityActivityAlert[];
  totalCount: number;
  communityId?: number;
};

const RecentAlertsColumn = ({ alerts, totalCount, communityId }: Props) => {
  const navigate = useNavigate();

  const goTo = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <Column data-test-id="activity-summary-alerts-column">
      <ColumnHeader>
        <ColumnTitle>Recent Alerts</ColumnTitle>
        <ColumnLink
          data-test-id="activity-summary-alerts-link"
          href={`/community/${communityId}/alerts`}
          onClick={goTo(`/community/${communityId}/alerts`)}
        >
          All Alerts ({totalCount}) <img src="/icons/chevron-right-blue.svg" width="16" height="16" alt="" />
        </ColumnLink>
      </ColumnHeader>
      <AlertsList>
        {alerts.length === 0 && (
          <EmptyMessage data-test-id="activity-summary-alerts-empty">No recent alerts.</EmptyMessage>
        )}
        {alerts.map((alert) => (
          <AlertRow key={alert.id} data-test-id={`activity-summary-alert-row-${alert.id}`}>
            <AlertAccent />
            <AlertContent>
              <AlertTitle>{alert.title}</AlertTitle>
              <AlertTime>{getRelativeTime(alert.created_at)}</AlertTime>
            </AlertContent>
          </AlertRow>
        ))}
      </AlertsList>
    </Column>
  );
};

export default RecentAlertsColumn;
